import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Trash2, Users, Search, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AdminRegistrados() {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/applicants"); setRows(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar la cuenta de ${r.name || r.email}? Se borrarán también sus ${r.applications} solicitud(es). Esta acción no se puede deshacer.`)) return;
    setDeleting(r.id);
    try {
      const { data } = await api.delete(`/admin/applicants/${r.id}`);
      toast.success(`Cuenta eliminada (${data.applications_removed} solicitud(es) borradas).`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setDeleting(null); }
  };

  const tempPassword = async (r) => {
    if (!window.confirm(`¿Generar una clave provisoria para ${r.name || r.email}? Su clave actual dejará de funcionar y la cuenta se desbloquea.`)) return;
    try {
      const { data } = await api.post(`/admin/applicants/${r.id}/temp-password`);
      window.prompt(`Clave provisoria para ${r.name || r.rut} (cópiala ahora, no se vuelve a mostrar). Al entrar deberá cambiarla:`, data.password);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = (rows || []).filter((r) => {
    if (!q.trim()) return true;
    const s = `${r.name || ""} ${r.email || ""} ${r.rut || ""}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  });

  return (
    <AdminLayout>
      <div className="flex items-center gap-3 mb-2">
        <Users className="w-6 h-6 text-accent" />
        <h1 className="text-2xl font-bold">Socios registrados</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Todas las cuentas creadas, hayan enviado una solicitud o no. {isSuper && "Como superadministrador puedes generar una clave provisoria (ícono de llave) o eliminar cuentas de prueba."}
      </p>

      <div className="relative max-w-md mb-4">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, correo o RUT"
          className="h-11 rounded-xl pl-9" data-testid="registrados-search" />
      </div>

      {rows === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="registrados-table">
              <thead className="bg-secondary/60 text-left">
                <tr className="text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">Nombre</th>
                  <th className="px-4 py-3 font-semibold">RUT</th>
                  <th className="px-4 py-3 font-semibold">Correo</th>
                  <th className="px-4 py-3 font-semibold">Base EIM</th>
                  <th className="px-4 py-3 font-semibold">Solicitudes</th>
                  <th className="px-4 py-3 font-semibold">Registrado</th>
                  {isSuper && <th className="px-4 py-3 font-semibold text-right">Acción</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={isSuper ? 7 : 6} className="px-4 py-10 text-center text-muted-foreground" data-testid="registrados-empty">No hay socios registrados que coincidan.</td></tr>
                ) : filtered.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`registrado-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium text-primary">{r.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.rut || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.email || "—"}</td>
                    <td className="px-4 py-3">{r.in_eim_base ? <span className="text-emerald-700 font-medium">Sí</span> : <span className="text-slate-400">No</span>}</td>
                    <td className="px-4 py-3">
                      {r.submitted > 0
                        ? <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-0.5 text-xs font-semibold">{r.submitted} enviada(s)</span>
                        : r.has_draft
                          ? <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-xs font-semibold">Borrador</span>
                          : <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-2.5 py-0.5 text-xs font-semibold">Sin solicitud</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString("es-CL") : "—"}</td>
                    {isSuper && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" title="Clave provisoria" onClick={() => tempPassword(r)} data-testid={`registrado-temp-pw-${r.id}`}>
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" disabled={deleting === r.id}
                          onClick={() => remove(r)} data-testid={`registrado-delete-${r.id}`}>
                          {deleting === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
